import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Plus, Info } from "lucide-react";
import { toast } from "sonner";

const BENEFIT_RISK_THRESHOLD = 6; // must mirror server BENEFIT_RISK_THRESHOLD

const CONTROL_TYPES = [
  {
    value: "inherently_safe_design",
    label: "1. Inherently safe design",
    hint: "First-line control: eliminate hazard through design (per ISO 14971 §7.1).",
  },
  {
    value: "protective_measure",
    label: "2. Protective measure",
    hint: "Second-line: engineered safeguard, interlock, alarm, guard, redundancy.",
  },
  {
    value: "information_for_safety",
    label: "3. Information for safety",
    hint: "Last-resort: labelling, IFU warning, training. Requires documented rationale.",
  },
];

const controlLabel = (t) => CONTROL_TYPES.find((c) => c.value === t)?.label || "—";

const EMPTY_FORM = {
  item_component: "",
  hazard: "",
  hazardous_situation: "",
  harm: "",
  severity: 3,
  probability: 3,
  risk_control_type: "",
  risk_control_description: "",
  verification_evidence: "",
  residual_severity: null,
  residual_probability: null,
  benefit_risk_justification: "",
  acceptability_reference: "",
};

export default function RiskManagement() {
  const [entries, setEntries] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    const [e, m] = await Promise.all([api.get("/risk/entries"), api.get("/risk/matrix")]);
    setEntries(e.data);
    setMatrix(m.data);
  };
  useEffect(() => { load(); }, []);

  const residualS = form.residual_severity ?? form.severity;
  const residualP = form.residual_probability ?? form.probability;
  const residualRpn = residualS * residualP;
  const requiresBenefitRisk = residualRpn >= BENEFIT_RISK_THRESHOLD;

  const canSubmit = useMemo(() => {
    if (!form.hazard.trim() || !form.hazardous_situation.trim() || !form.harm.trim()) return false;
    if (form.risk_control_description.trim() && !form.risk_control_type) return false;
    if (requiresBenefitRisk && !form.benefit_risk_justification.trim()) return false;
    return true;
  }, [form, requiresBenefitRisk]);

  const create = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/risk/entries", {
        ...form,
        risk_control_type: form.risk_control_type || null,
        residual_severity: form.residual_severity || form.severity,
        residual_probability: form.residual_probability || form.probability,
      });
      toast.success("Hazard logged");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save risk entry");
    } finally {
      setSaving(false);
    }
  };

  const cellColor = (s, p) => {
    const rpn = (s + 1) * (p + 1);
    if (rpn >= 15) return "bg-red-500 text-white";
    if (rpn >= 8) return "bg-amber-400 text-slate-900";
    if (rpn >= 4) return "bg-yellow-200 text-slate-700";
    return "bg-emerald-100 text-emerald-800";
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="vq-overline mb-2">ISO 14971 · Risk register</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Risk Management</h1>
            <p className="text-slate-600 mt-2">
              Hazard identification, risk estimation, hierarchy-of-control mitigation, and residual risk reduced as far as possible (AFAP).
            </p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="risk-new-btn" className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Log hazard
          </Button>
        </div>

        {/* Matrix */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="vq-card p-6">
            <h3 className="font-display font-bold text-lg text-slate-900 mb-1">Initial risk estimation</h3>
            <p className="text-xs text-slate-500 mb-4">Pre-mitigation severity × probability distribution.</p>
            <RiskMatrix data={matrix?.matrix} cellColor={cellColor} testId="matrix-initial" />
          </div>
          <div className="vq-card p-6">
            <h3 className="font-display font-bold text-lg text-slate-900 mb-1">Residual risk evaluation</h3>
            <p className="text-xs text-slate-500 mb-4">Post-control (AFAP). Residual RPN ≥ {BENEFIT_RISK_THRESHOLD} requires benefit-risk justification.</p>
            <RiskMatrix data={matrix?.residual_matrix} cellColor={cellColor} testId="matrix-residual" />
          </div>
        </div>

        {/* Entries */}
        <div className="vq-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">ID</th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Item / Component</th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Hazard → Hazardous situation → Harm</th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Initial S×P</th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Initial RPN</th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Risk control (hierarchy)</th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Verification</th>
                  <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Residual RPN</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-slate-500">
                      No hazards logged yet.
                    </td>
                  </tr>
                ) : entries.map((e, idx) => {
                  const rpnHigh = e.rpn >= 15;
                  const rpnMed = e.rpn >= 8;
                  const rrpnHigh = e.residual_rpn >= 15;
                  const rrpnMed = e.residual_rpn >= 8;
                  const rrpnAcceptable = e.residual_rpn < BENEFIT_RISK_THRESHOLD;
                  return (
                    <tr key={e.risk_id} className="border-b border-slate-100 align-top">
                      <td className="px-4 py-3 text-xs font-mono text-slate-500">
                        HAZ-{String(idx + 1).padStart(3, "0")}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{e.item_component || "—"}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-semibold text-slate-900">{e.hazard}</div>
                        <div className="text-xs text-slate-500 mt-0.5">↳ {e.hazardous_situation}</div>
                        <div className="text-xs text-slate-500 mt-0.5">↳ {e.harm}</div>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-700">{e.severity}×{e.probability}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${rpnHigh ? "bg-red-100 text-red-700" : rpnMed ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {e.rpn}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 max-w-xs">
                        <div className="text-xs font-mono uppercase text-slate-500">{controlLabel(e.risk_control_type)}</div>
                        <div className="mt-0.5">{e.risk_control_description || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[180px]">
                        {e.verification_evidence || <span className="text-slate-400">not linked</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-mono ${rrpnHigh ? "bg-red-100 text-red-700" : rrpnMed ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {e.residual_rpn}
                        </span>
                        <div className="text-[10px] text-slate-500 mt-1">
                          {rrpnAcceptable
                            ? "Broadly acceptable · AFAP"
                            : (e.benefit_risk_justification || "").trim()
                              ? "Benefit-risk documented"
                              : "Requires benefit-risk justification"}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-slate-100 bg-slate-50 text-xs text-slate-500 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Language and column order follow ISO 14971. Residual risk is documented as reduced <b>as far as possible (AFAP)</b> — never as &ldquo;zero risk&rdquo;. Where residual RPN ≥ {BENEFIT_RISK_THRESHOLD}, a benefit-risk justification per §8 is mandatory.
            </span>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!saving) setOpen(v); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Log hazard (ISO 14971)</DialogTitle>
            <DialogDescription>
              Document one hazard chain per entry. Use clinical/technical terms — avoid corporate risk language like &ldquo;Issue&rdquo; or &ldquo;Threat&rdquo;.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Item / Component</Label>
              <Input
                data-testid="risk-item"
                value={form.item_component}
                onChange={(e) => setForm({ ...form, item_component: e.target.value })}
                placeholder="e.g. Sterile barrier · pH sensor · software module"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Hazard <span className="text-red-600">*</span></Label>
              <Input
                data-testid="risk-hazard"
                value={form.hazard}
                onChange={(e) => setForm({ ...form, hazard: e.target.value })}
                placeholder="e.g. Residual proteinaceous contaminant"
                className="mt-1.5"
              />
              <p className="text-[11px] text-slate-500 mt-1">Source of potential harm — biological, chemical, electrical, mechanical, operational.</p>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Hazardous situation <span className="text-red-600">*</span></Label>
              <Input
                data-testid="risk-situation"
                value={form.hazardous_situation}
                onChange={(e) => setForm({ ...form, hazardous_situation: e.target.value })}
                placeholder="e.g. Patient exposed to unverified sterile barrier during use"
                className="mt-1.5"
              />
            <p className="text-[11px] text-slate-500 mt-1">Foreseeable sequence of events that exposes the user/patient to the hazard.</p>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Harm <span className="text-red-600">*</span></Label>
              <Input
                data-testid="risk-harm"
                value={form.harm}
                onChange={(e) => setForm({ ...form, harm: e.target.value })}
                placeholder="e.g. Local tissue inflammation, allergic reaction, or systemic infection"
                className="mt-1.5"
              />
              <p className="text-[11px] text-slate-500 mt-1">Clinical endpoint — physical injury or damage to health, property, or environment.</p>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">Initial risk estimation (pre-control)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Severity (1-5)</Label>
                  <Input
                    data-testid="risk-severity"
                    type="number" min={1} max={5} value={form.severity}
                    onChange={(e) => setForm({ ...form, severity: Math.max(1, Math.min(5, parseInt(e.target.value) || 1)) })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Probability (1-5)</Label>
                  <Input
                    data-testid="risk-probability"
                    type="number" min={1} max={5} value={form.probability}
                    onChange={(e) => setForm({ ...form, probability: Math.max(1, Math.min(5, parseInt(e.target.value) || 1)) })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="text-xs font-mono text-slate-500 mt-2">
                Initial RPN = <b className="text-slate-900">{form.severity * form.probability}</b>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">Risk control (hierarchy per §7.1)</Label>
              <Select
                value={form.risk_control_type}
                onValueChange={(v) => setForm({ ...form, risk_control_type: v })}
              >
                <SelectTrigger data-testid="risk-control-type" className="mt-1">
                  <SelectValue placeholder="Select hierarchy level…" />
                </SelectTrigger>
                <SelectContent>
                  {CONTROL_TYPES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.risk_control_type && (
                <p className="text-[11px] text-slate-500 mt-1.5">
                  {CONTROL_TYPES.find((c) => c.value === form.risk_control_type).hint}
                </p>
              )}
              <div className="mt-3">
                <Label className="text-xs text-slate-600">Risk control description</Label>
                <Textarea
                  data-testid="risk-control-description"
                  value={form.risk_control_description}
                  onChange={(e) => setForm({ ...form, risk_control_description: e.target.value })}
                  rows={2}
                  placeholder="e.g. Automated interlock preventing use until sterile-barrier verification passes"
                  className="mt-1 resize-none"
                />
              </div>
              <div className="mt-3">
                <Label className="text-xs text-slate-600">Verification of implementation & effectiveness</Label>
                <Input
                  data-testid="risk-verification"
                  value={form.verification_evidence}
                  onChange={(e) => setForm({ ...form, verification_evidence: e.target.value })}
                  placeholder="e.g. Passed via Protocol V-102 · DHF Design Review #4"
                  className="mt-1"
                />
                <p className="text-[11px] text-slate-500 mt-1">Objective proof: test protocol pass, design review, clinical data line.</p>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">Residual risk evaluation (post-control, AFAP)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Residual severity</Label>
                  <Input
                    data-testid="risk-res-severity"
                    type="number" min={1} max={5}
                    value={form.residual_severity ?? ""}
                    placeholder={String(form.severity)}
                    onChange={(e) => setForm({ ...form, residual_severity: e.target.value ? Math.max(1, Math.min(5, parseInt(e.target.value))) : null })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Residual probability</Label>
                  <Input
                    data-testid="risk-res-probability"
                    type="number" min={1} max={5}
                    value={form.residual_probability ?? ""}
                    placeholder={String(form.probability)}
                    onChange={(e) => setForm({ ...form, residual_probability: e.target.value ? Math.max(1, Math.min(5, parseInt(e.target.value))) : null })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="text-xs font-mono text-slate-500 mt-2">
                Residual RPN = <b className={`${requiresBenefitRisk ? "text-amber-700" : "text-emerald-700"}`}>{residualRpn}</b>
                {" · "}
                {requiresBenefitRisk ? "benefit-risk justification required" : "broadly acceptable (AFAP)"}
              </div>
            </div>

            {requiresBenefitRisk && (
              <div className="border-l-4 border-l-amber-500 bg-amber-50 p-3 rounded" data-testid="risk-benefit-block">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-900">
                    Residual risk ≥ {BENEFIT_RISK_THRESHOLD}. Document why the risk is acceptable given the clinical benefit (ISO 14971 §8).
                  </div>
                </div>
                <Label className="text-xs font-mono uppercase tracking-wider text-amber-800">Benefit-risk justification <span className="text-red-600">*</span></Label>
                <Textarea
                  data-testid="risk-benefit-justification"
                  value={form.benefit_risk_justification}
                  onChange={(e) => setForm({ ...form, benefit_risk_justification: e.target.value })}
                  rows={3}
                  placeholder='e.g. "Weighing the clinical benefits of continuous ICU EEG monitoring against the residual risk of skin irritation, the residual risk is judged acceptable per Risk Management Plan §4.3."'
                  className="mt-1 resize-none"
                />
                <div className="mt-2">
                  <Label className="text-xs text-amber-800">Acceptability reference</Label>
                  <Input
                    data-testid="risk-acceptability-ref"
                    value={form.acceptability_reference}
                    onChange={(e) => setForm({ ...form, acceptability_reference: e.target.value })}
                    placeholder="e.g. Risk Management Plan §4.3"
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={create}
              data-testid="risk-submit"
              disabled={!canSubmit || saving}
              className="bg-slate-900 hover:bg-[#0055FF] text-white"
            >
              {saving ? "Saving…" : "Log hazard"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function RiskMatrix({ data, cellColor, testId }) {
  const rows = data || Array.from({ length: 5 }, () => [0, 0, 0, 0, 0]);
  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-6 gap-1 text-xs">
        <div />
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} className="text-center font-mono text-slate-500">S{s}</div>
        ))}
        {[5, 4, 3, 2, 1].map((p) => (
          <div key={`row-${p}`} className="contents">
            <div className="text-right pr-2 font-mono text-slate-500 self-center">P{p}</div>
            {[0, 1, 2, 3, 4].map((s) => (
              <div key={`c${p}-${s}`} className={`aspect-square flex items-center justify-center rounded font-mono font-bold ${cellColor(s, p - 1)}`}>
                {rows[p - 1][s]}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-4 text-[10px] font-mono text-slate-500 flex flex-wrap gap-3">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-100 rounded" /> Broadly acceptable</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-200 rounded" /> Tolerable</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-400 rounded" /> Undesirable</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded" /> Intolerable</span>
      </div>
    </div>
  );
}
